/**
 * Keeps React running while the browser translates the page.
 *
 * Chrome Translate replaces text nodes React owns with `<font>` wrappers. React
 * still holds the original node, so its next `removeChild` or `insertBefore`
 * against it throws `NotFoundError` and the app falls into the error boundary
 * (facebook/react#11538). Staff read German customer mail through translation,
 * so blocking it with `translate="no"` is not an option.
 *
 * Once the page has been translated, an operation on a node that is no longer a
 * child is skipped (remove) or turned into an append (insert), and logged at
 * warn level so Sentry Logs shows how often it happens. The tolerance lasts until
 * reload: after "Show original" React still holds nodes translation replaced. In
 * a tab never translated the native call runs unchanged, so a moved node from our
 * own code still throws.
 */
export function installTranslationGuard() {
  // Shared by both patched methods: one flag per page, set once translation is seen, never reset.
  let wasTranslated = isTranslated();
  const watcher = new MutationObserver(() => {
    if (!isTranslated()) return;
    wasTranslated = true;
    watcher.disconnect();
  });
  watcher.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  const tolerates = () => wasTranslated || isTranslated();

  // The originals are taken unbound on purpose: each call rebinds them to its own node.
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const removeChild = Node.prototype.removeChild;
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const insertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this && tolerates()) {
      console.warn("translationGuard: skipped removeChild of a node translation replaced");
      return child;
    }
    removeChild.call(this, child);
    return child;
  };

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    if (child && child.parentNode !== this && tolerates()) {
      console.warn("translationGuard: appended instead of insertBefore a node translation replaced");
      return this.appendChild(node);
    }
    insertBefore.call(this, node, child);
    return node;
  };
}

/** Chrome Translate marks the root element while it has translated the page. */
function isTranslated() {
  const { classList } = document.documentElement;
  return classList.contains("translated-ltr") || classList.contains("translated-rtl");
}
